import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Menu, X, User, ChevronDown } from 'lucide-react';
import CallbackModal from '@/components/layout/CallbackModal';

export default function Navbar() {
  const { isAuthenticated, isStaff, user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tailoringOpen, setTailoringOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);

  // "Over hero" is only true on the home route, before the user has scrolled past the hero
  const isHomeHero = location.pathname === '/';
  const transparent = isHomeHero && !scrolled;

  useEffect(() => {
    api.get('/categories').then((res) => setCategories(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setTailoringOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkBase = 'nav-underline uppercase font-medium tracking-luxe text-[14px] transition-colors duration-300';
  const linkColor = transparent ? 'text-white/95 hover:text-white' : 'text-ink hover:text-ink';

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          transparent ? 'bg-transparent' : 'bg-background/95 backdrop-blur-sm shadow-[0_1px_0_hsl(var(--hairline))]'
        }`}
        style={{ height: transparent ? 88 : 72 }}
      >
        <div className="max-w-[1440px] mx-auto h-full px-6 lg:px-12 flex items-center justify-between">
          {/* LEFT LINKS (desktop) */}
          <div className="hidden md:flex items-center gap-10 flex-1">
            <Link to="/" data-testid="nav-home" className={`${linkBase} ${linkColor} ${location.pathname === '/' ? 'active' : ''}`}>
              Home
            </Link>
            <div className="relative" ref={dropdownRef} onMouseEnter={() => setTailoringOpen(true)} onMouseLeave={() => setTailoringOpen(false)}>
              <Link
                to="/categories"
                data-testid="nav-tailoring"
                className={`flex items-center gap-1.5 ${linkBase} ${linkColor} ${location.pathname.startsWith('/categories') ? 'active' : ''}`}
              >
                Tailoring <ChevronDown className={`w-3.5 h-3.5 transition-transform ${tailoringOpen ? 'rotate-180' : ''}`} />
              </Link>
              {tailoringOpen && (
                <div className="absolute top-full left-0 pt-3 z-50">
                  <div
                    className="bg-background border border-border shadow-sm py-4"
                    data-testid="tailoring-dropdown"
                    style={{ width: 260 }}
                  >
                    <Link
                      to="/categories"
                      className="block px-6 py-2 uppercase tracking-luxe text-[11px] font-semibold text-gold hover:text-gold-hover"
                      onClick={() => setTailoringOpen(false)}
                    >
                      All Categories
                    </Link>
                    <div className="border-t border-border my-2" />
                    {categories.slice(0, 11).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/categories/${cat.slug}`}
                        className="block px-6 py-2 font-display text-ink hover:text-gold transition-colors"
                        style={{ fontSize: 20, fontWeight: 500 }}
                        onClick={() => setTailoringOpen(false)}
                        data-testid={`nav-cat-${cat.slug}`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/about" data-testid="nav-about" className={`${linkBase} ${linkColor} ${location.pathname === '/about' ? 'active' : ''}`}>
              About
            </Link>
          </div>

          {/* CENTER LOGO */}
          <Link
            to="/"
            data-testid="logo"
            className={`font-display font-medium tracking-[0.04em] transition-colors duration-300 ${transparent ? 'text-white' : 'text-ink'} md:absolute md:left-1/2 md:-translate-x-1/2`}
            style={{ fontSize: transparent ? 44 : 40, lineHeight: 1 }}
          >
            Besq
          </Link>

          {/* RIGHT LINKS + CTA */}
          <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
            {isAuthenticated ? (
              <div className="relative group">
                <button className={`flex items-center gap-1.5 ${linkBase} ${linkColor}`} data-testid="nav-user-menu">
                  <User className="w-4 h-4" />
                  {user?.name || 'Account'} <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <div className="absolute top-full right-0 pt-3 w-52 z-50 hidden group-hover:block">
                  <div className="bg-background border border-border py-2">
                    <Link to="/profile" className="block px-5 py-2 text-[13px] tracking-wide text-ink hover:text-gold" data-testid="nav-profile">Profile</Link>
                    <Link to="/orders" className="block px-5 py-2 text-[13px] tracking-wide text-ink hover:text-gold" data-testid="nav-orders">My Orders</Link>
                    {isStaff && <Link to="/admin" className="block px-5 py-2 text-[13px] tracking-wide font-medium text-gold" data-testid="nav-admin">Admin Panel</Link>}
                    <div className="border-t border-border my-1" />
                    <button onClick={logout} className="w-full text-left px-5 py-2 text-[13px] tracking-wide text-destructive hover:bg-secondary" data-testid="nav-logout">Logout</button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" className={`${linkBase} ${linkColor}`} data-testid="nav-login">Login</Link>
            )}

            <button
              onClick={() => setCallbackOpen(true)}
              data-testid="arrange-callback-btn"
              className={`uppercase tracking-luxe text-[13px] font-medium border px-6 py-3 transition-all duration-300 ${
                transparent
                  ? 'text-white border-white/70 hover:bg-white hover:text-ink'
                  : 'text-ink border-ink hover:bg-ink hover:text-background'
              }`}
            >
              Arrange a Callback
            </button>
          </div>

          {/* MOBILE toggle — z-[70] keeps it above the fixed overlay below (z-[60]) */}
          <button
            className={`md:hidden relative z-[70] ${transparent ? 'text-white' : 'text-ink'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* MOBILE full-screen menu — MUST be a sibling of <nav>, not a child.
          <nav> uses backdrop-blur which creates a containing block for `position:fixed`
          descendants, breaking the full-viewport overlay. Rendering the menu outside
          the nav restores viewport-relative fixed positioning. */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-0 bg-background z-[60] pt-20 px-6 overflow-y-auto" data-testid="mobile-menu">
            {/* Close button — sibling of nav, so it must live inside the overlay itself */}
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="absolute top-6 right-6 text-ink hover:text-gold transition-colors"
            >
              <X className="w-7 h-7" />
            </button>
            <div className="flex flex-col gap-2 pt-8">
              <Link to="/" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">Home</Link>
              <Link to="/categories" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">Tailoring</Link>
              <div className="pl-6 py-1 flex flex-col gap-1">
                {categories.slice(0, 11).map((cat) => (
                  <Link key={cat.id} to={`/categories/${cat.slug}`} onClick={() => setMobileOpen(false)} className="block py-1.5 text-[15px] text-ink-muted uppercase tracking-luxe font-medium">
                    {cat.name}
                  </Link>
                ))}
              </div>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">About</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">Profile</Link>
                  <Link to="/orders" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">My Orders</Link>
                  {isStaff && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-gold">Admin Panel</Link>}
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="text-left py-3 font-display text-3xl text-destructive">Logout</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-3 font-display text-3xl text-ink">Login</Link>
              )}
              <button
                onClick={() => { setCallbackOpen(true); setMobileOpen(false); }}
                data-testid="mobile-callback-btn"
                className="mt-8 uppercase tracking-luxe text-sm font-medium border border-ink text-ink px-6 py-4 self-start"
              >
                Arrange a Callback
              </button>
            </div>
          </div>
      )}
      <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} />
    </>
  );
}
